import { Suspense, useContext, useEffect } from 'react';
import type { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Routes as ReactRoutes, Route } from 'react-router-dom';

import { useFlag } from '@fusion/feature-flags';

import { UnleashKeys } from '../constants/unleash-keys';
import { AppConfigErrorScreen } from '../features/app-config/app-config-error-screen/cdm-app-config-error-screen';
import { useLocations } from '../features/app-config/hooks/cdm-use-locations';
import { useFeatureToggleConfig } from '../features/app-config/hooks/use-query/cdm-use-feature-toggle-config';
import { useSelectedRootLocationConfiguration } from '../features/app-config/hooks/use-selected-root-location-configuration';
import {
  cdmChecklistItemNewObservationRoute,
  cdmChecklistItemObservationDetailsRoute,
  cdmChecklistItemObservationListRoute,
} from '../features/checklist/cdm-checklist-item-observation-routes.utils';
import { useIsSchedulerEnabled } from '../features/checklist/checklist-scheduler/hooks/use-is-scheduler-enabled';
import { useFeatureObservationsOnIdm } from '../hooks/feature-flags/use-feature-observations-on-idm';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { useCurrentUserContext } from '../providers/current-user-provider/current-user-provider';

import {
  ActivityChecklistMobile,
  ActivityDesktop,
  ActivityDetailsView,
  ActivityListView,
  ActivityPlanningView,
  Asset3d,
  AssetActivities,
  AssetDetails,
  AssetDocuments,
  AssetNotifications,
  AssetProperties,
  AssetTrends,
  ChecklistItemObservationFormPage,
  ChecklistItemObservationListPage,
  DataModelingOverridePage,
  IdmObservationMobileOverviewPage,
  IdmObservationsDesktopFormPage,
  IdmObservationsDesktopTablePage,
  IdmObservationsMobileListPage,
  LandingPage,
  NotificationDetailsView,
  PageLayout,
  PageNotFound,
  SchedulesPage,
  TemplateCreatedConfirmationPage,
  TemplatesMobile,
  TemplatesOverviewNew,
  UserProfilePage,
} from './cdm-lazy-route-pages';
import type { CdmRoutesContextInterface } from './cdm-routes.context';
import { CdmRoutesContext } from './cdm-routes.context';
import {
  getIsChecklistPageEnabled,
  getIsObservationsFeatureEnabledOnIdm,
  getIsOverviewPageEnabled,
  getIsTemplatesPageEnabled,
  getLandingPagePath,
} from './cdm-utils';
import { useHasUserEnabledAtlasHome } from './landing-page/hooks/use-has-user-enabled-atlas-home';
import { PageLoader } from './page-loader/page-loader';
import { SchedulesErrorBoundary } from './schedules/schedules-error-boundary';

export const cdmRoutesContextDefaultValue: CdmRoutesContextInterface = {
  useIsDesktop,
  useFeatureObservationsOnIdm,
  useSelectedRootLocationConfiguration,
  useCurrentUserContext,
  useLocations,
  useFeatureToggleConfig,
  useIsSchedulerEnabled,
  useFlag,
  useHasUserEnabledAtlasHome,
  AppConfigErrorScreen,
  PageLayout,
  LandingPage,
  UserProfilePage,
  ActivityDesktop,
  ActivityListView,
  ActivityPlanningView,
  ActivityDetailsView,
  TemplatesMobile,
  TemplatesOverviewNew,
  TemplateCreatedConfirmationPage,
  SchedulesPage,
  AssetDetails,
  Asset3d,
  AssetTrends,
  AssetProperties,
  AssetActivities,
  AssetNotifications,
  NotificationDetailsView,
  AssetDocuments,
  ActivityChecklistMobile,
  IdmObservationsDesktopTablePage,
  IdmObservationsDesktopFormPage,
  IdmObservationsMobileListPage,
  IdmObservationMobileOverviewPage,
  PageNotFound,
  DataModelingOverridePage,
  ChecklistItemObservationFormPage,
  ChecklistItemObservationListPage,
};

export const CdmRoutes: FunctionComponent = () => {
  const {
    useIsDesktop,
    useFeatureObservationsOnIdm,
    useSelectedRootLocationConfiguration,
    useCurrentUserContext,
    useLocations,
    useFeatureToggleConfig,
    useIsSchedulerEnabled,
    useFlag,
    useHasUserEnabledAtlasHome,
    AppConfigErrorScreen,
    PageLayout,
    LandingPage,
    UserProfilePage,
    ActivityDesktop,
    ActivityListView,
    ActivityPlanningView,
    ActivityDetailsView,
    TemplatesMobile,
    TemplatesOverviewNew,
    TemplateCreatedConfirmationPage,
    SchedulesPage,
    AssetDetails,
    Asset3d,
    AssetTrends,
    AssetProperties,
    AssetActivities,
    AssetNotifications,
    NotificationDetailsView,
    AssetDocuments,
    ActivityChecklistMobile,
    IdmObservationsDesktopTablePage,
    IdmObservationsDesktopFormPage,
    IdmObservationsMobileListPage,
    IdmObservationMobileOverviewPage,
    PageNotFound,
    DataModelingOverridePage,
    ChecklistItemObservationFormPage,
    ChecklistItemObservationListPage,
  } = useContext(CdmRoutesContext) ?? cdmRoutesContextDefaultValue;

  const isDesktop = useIsDesktop();
  const isDailyBriefEnabled = useFlag(UnleashKeys.atlas_daily_brief);
  const { isEnabled: hasUserEnabledAtlasHome } = useHasUserEnabledAtlasHome();

  const isObservationsFeatureIdmFeatureFlagEnabled =
    useFeatureObservationsOnIdm();
  const { t } = useTranslation('user');

  const selectedRootLocation = useSelectedRootLocationConfiguration();
  const { user: userData } = useCurrentUserContext();
  const { handleSaveLocation } = useLocations();

  const {
    config: rootLocationFeatureToggles,
    isLoading: isFeatureConfigLoading,
  } = useFeatureToggleConfig();

  const {
    isEnabled: isSchedulesPageEnabled,
    isLoading: isSchedulesPageEnabledLoading,
  } = useIsSchedulerEnabled(UnleashKeys.schedule_tab);

  useEffect(() => {
    if (isFeatureConfigLoading) return;
    if (selectedRootLocation !== undefined) return;

    const userLocationPreference = userData?.preferences?.infield?.location;
    if (userLocationPreference === undefined) return;

    // We have no valid selected root location. It might be because the user preference location is not within the config.
    // Clear the preferences to find out.
    handleSaveLocation(undefined);
  }, [
    handleSaveLocation,
    isFeatureConfigLoading,
    selectedRootLocation,
    userData?.preferences?.infield?.location,
  ]);

  const isObservationsFeatureEnabledOnIdm =
    getIsObservationsFeatureEnabledOnIdm(
      isObservationsFeatureIdmFeatureFlagEnabled,
      rootLocationFeatureToggles
    );
  const isOverviewPageEnabled = getIsOverviewPageEnabled(
    rootLocationFeatureToggles
  );
  const isChecklistPageEnabled = getIsChecklistPageEnabled(
    rootLocationFeatureToggles
  );
  const isTemplatesPageEnabled = getIsTemplatesPageEnabled(
    rootLocationFeatureToggles
  );

  const landingPagePath = getLandingPagePath({
    isDailyBriefEnabled,
    hasUserEnabledAtlasHome,
    isChecklistPageEnabled,
    isDesktop,
    isObservationsFeatureEnabledOnIdm,
    selectedRootLocation,
  });

  if (isFeatureConfigLoading) {
    return (
      <PageLoader
        infoText={t(
          'loadingFeatureConfiguration',
          'Loading configured application features...'
        )}
      />
    );
  }

  if (selectedRootLocation === undefined) {
    return (
      <AppConfigErrorScreen
        title={t(
          'USER_ERROR_LOCATION_MISSING_TITLE',
          'Error: Location is missing.'
        )}
        body={t(
          'USER_ERROR_LOCATION_MISSING_DESCRIPTION',
          "The InField admin has not defined any location yet, or you dont't have access to any of the configured locations. Contact your admin if you have any questions."
        )}
      />
    );
  }

  return (
    <Suspense
      fallback={
        <PageLoader
          infoText={t('LOADING_APPLICATION', 'loading application...')}
        />
      }
    >
      <ReactRoutes>
        <Route path="/" element={<PageLayout page="mobile-explore" />}>
          <Route
            path="/"
            element={
              !selectedRootLocation ? (
                <PageLayout />
              ) : (
                <Navigate to={landingPagePath} />
              )
            }
          />
          <Route path="/explore" element={<LandingPage />} />
        </Route>

        <Route path="/" element={<PageLayout />}>
          {/* Atlas AI maps some of these paths in surface-registry.ts
              (/activity/.../details, /observation(s), /asset/...). Those
              matchers are duplicated regexes — update both if a path
              changes. Nested checklist-item observation routes are shared
              constants and stay in sync automatically. */}
          <Route path="/profile" element={<UserProfilePage />} />
          <Route
            path="/profile/data-modeling-override"
            element={<DataModelingOverridePage />}
          />
          {isChecklistPageEnabled && (
            <Route
              path="/checklists"
              element={isDesktop ? <ActivityDesktop /> : <ActivityListView />}
            />
          )}
          {isOverviewPageEnabled && (
            <Route path="/overview" element={<ActivityPlanningView />} />
          )}
          {rootLocationFeatureToggles?.workorders && (
            <Route
              path="/activity/:activityId/details"
              element={<ActivityDetailsView />}
            />
          )}
          {isTemplatesPageEnabled && (
            <>
              <Route
                path="/templates"
                element={
                  !isDesktop ? <TemplatesMobile /> : <TemplatesOverviewNew />
                }
              />
              <Route
                path="/templates/createdChecklist/:checklistExternalId"
                element={<TemplateCreatedConfirmationPage />}
              />
            </>
          )}
          {isSchedulesPageEnabled && (
            <Route
              path="/schedules"
              element={
                <SchedulesErrorBoundary>
                  <SchedulesPage />
                </SchedulesErrorBoundary>
              }
            />
          )}
          {!isSchedulesPageEnabled && !isSchedulesPageEnabledLoading && (
            <Route path="/schedules" element={<Navigate to="/checklists" />} />
          )}
          {isObservationsFeatureEnabledOnIdm && (
            <>
              <Route
                path="/observations"
                element={
                  isDesktop ? (
                    <IdmObservationsDesktopTablePage />
                  ) : (
                    <IdmObservationsMobileListPage />
                  )
                }
              />
              <Route
                path="/observation"
                element={
                  isDesktop ? (
                    <IdmObservationsDesktopFormPage />
                  ) : (
                    <IdmObservationMobileOverviewPage />
                  )
                }
              />
              <Route
                path="/observation/:observationExternalId"
                element={
                  isDesktop ? (
                    <IdmObservationsDesktopFormPage />
                  ) : (
                    <IdmObservationMobileOverviewPage />
                  )
                }
              />
              <Route
                path={cdmChecklistItemNewObservationRoute}
                element={<ChecklistItemObservationFormPage />}
              />
              <Route
                path={cdmChecklistItemObservationDetailsRoute}
                element={<ChecklistItemObservationFormPage />}
              />
              <Route
                path={cdmChecklistItemObservationListRoute}
                element={<ChecklistItemObservationListPage />}
              />
            </>
          )}
          <Route path="/asset/:assetId" element={<AssetDetails />} />
          {rootLocationFeatureToggles?.threeD && (
            <Route path="/asset/:assetId/3d" element={<Asset3d />} />
          )}
          {rootLocationFeatureToggles?.trends && (
            <Route path="/asset/:assetId/trends" element={<AssetTrends />} />
          )}
          <Route
            path="/asset/:assetId/properties"
            element={<AssetProperties />}
          />
          {rootLocationFeatureToggles?.workorders && (
            <Route
              path="/asset/:assetId/activities"
              element={<AssetActivities />}
            />
          )}
          {rootLocationFeatureToggles?.notifications && (
            <Route
              path="/asset/:assetId/notifications"
              element={<AssetNotifications />}
            />
          )}
          {rootLocationFeatureToggles?.notifications && (
            <Route
              path="/notification/:notificationId/details"
              element={<NotificationDetailsView />}
            />
          )}
          {rootLocationFeatureToggles?.documents && (
            <Route
              path="/asset/:assetId/documents"
              element={<AssetDocuments />}
            />
          )}
        </Route>

        {isChecklistPageEnabled && (
          <Route path="/" element={<PageLayout page="mobile-checklist" />}>
            {/* Atlas AI checklist-perform surface. Keep in sync with
                surface-registry.ts — TypeScript will not catch a mismatch. */}
            <Route
              id="checklist"
              path="/activity/:activityId/checklist"
              element={<ActivityChecklistMobile />}
            />
          </Route>
        )}

        {isSchedulesPageEnabledLoading && (
          <Route
            path="/schedules"
            element={
              <PageLoader
                infoText={t(
                  'loadingSchedulerAccess',
                  'Checking your access...'
                )}
              />
            }
          />
        )}

        {/* Catch-all route for 404 Not Found */}
        <Route path="*" element={<PageNotFound />} />
      </ReactRoutes>
    </Suspense>
  );
};
